import { useEffect, useId, useRef, useState } from 'react'
import type { MediaAssetSummary } from '@cms/contracts'
import { ApiError, newIdempotencyKey } from '../api/client.js'
import { findAssetByPath as findByPath, rememberAsset } from '../api/asset-lookup.js'
import { mediaFileUrl, uploadMedia } from '../api/library.js'
import { useCan } from '../auth/session.js'
import { isBag } from '../domain/humane.js'
import { liveUrl } from '../domain/live-url.js'
import { ACCEPT_ATTRIBUTE, altFor, formatBytes, libraryPathFromValue, mediaDisplayName, mediaKindOf, uploadProblem } from '../domain/media.js'
import { useI18n } from '../i18n/context.js'
import { useSiteWorkspace } from '../workspace/site.js'
import { Button, Icon, IconButton, LinearProgress, cx } from '../ui/primitives.js'
import { AiImageDialog } from './AiImageDialog.js'
import { MediaPickerDialog } from './MediaPickerDialog.js'

/**
 * The image field of a content: a preview, "Scegli dalla libreria", "Carica",
 * and the address itself, which can still be typed or pasted — a migrated
 * page may point at a file the library does not have yet.
 */
export function MediaFieldControl({ label, value, onChange, disabled, required, supporting, large }: {
  label: string
  value: unknown
  onChange: (value: unknown) => void
  disabled?: boolean
  required?: boolean
  supporting?: string
  /** The big thumbnail of the main image at the top of a page. */
  large?: boolean
}) {
  const { t, locale } = useI18n()
  const { siteId, site } = useSiteWorkspace()
  const canUpload = useCan('cms:media:write')
  const id = useId()
  const fileInput = useRef<HTMLInputElement | null>(null)
  // A migrated page can carry the picture as `{ src, alt }`: edit the address and keep the rest of the object.
  const addressKey = isBag(value) ? (['src', 'url', 'path'].find((key) => typeof value[key] === 'string') ?? 'src') : null
  const text = typeof value === 'string' ? value : isBag(value) && addressKey && typeof value[addressKey] === 'string' ? value[addressKey] as string : ''
  const write = (next: string) => onChange(isBag(value) && addressKey ? { ...value, [addressKey]: next } : next)
  const [asset, setAsset] = useState<MediaAssetSummary | null>(null)
  const [picking, setPicking] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [progress, setProgress] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [previewFailed, setPreviewFailed] = useState(false)

  useEffect(() => {
    setPreviewFailed(false)
    const path = libraryPathFromValue(text)
    if (!path || !siteId) { setAsset(null); return }
    if (asset?.path === path) return
    let cancelled = false
    const timer = window.setTimeout(() => {
      void findByPath(siteId, path).then((found) => { if (!cancelled) setAsset(found) })
    }, 250)
    return () => { cancelled = true; window.clearTimeout(timer) }
  }, [text, siteId])

  const choose = (picked: MediaAssetSummary) => {
    rememberAsset(siteId, picked)
    setAsset(picked)
    setError(null)
    write(picked.publicPath)
  }

  const upload = async (file: File | undefined) => {
    if (!file) return
    const problem = uploadProblem(file)
    if (problem) { setError(t(`media.upload.problems.${problem}`, { size: formatBytes(file.size, locale) })); return }
    setError(null)
    setProgress(0)
    try {
      const result = await uploadMedia(
        siteId,
        { file, reason: t('media.reason.uploadForField', { name: file.name, field: label }) },
        newIdempotencyKey(),
        (loaded, total) => setProgress(total ? loaded / total : 0),
      )
      choose({ ...result.asset, updatedAt: Date.now() })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.network'))
    } finally {
      setProgress(null)
    }
  }

  // The preview: the library's copy when the path is one of its files, the
  // published site's otherwise, the address itself when it is a full URL.
  const trimmed = text.trim()
  const previewSource = asset && asset.sha256 ? mediaFileUrl(asset.id)
    : /^https?:\/\//i.test(trimmed) ? trimmed
      : trimmed.startsWith('/') ? liveUrl(site?.primaryDomain, trimmed) : null
  const kind = asset ? mediaKindOf(asset.mimeType) : 'image'
  const help = error ?? supporting ?? t('fields.mediaHelp')
  const alt = asset ? altFor(asset.alt, locale) : ''

  return <div className={cx('field', 'media-field', large && 'media-field-large', error && 'field-invalid')}>
    <div className="field-label-row">
      <label className="field-label" htmlFor={id}>{label}{required && <span className="field-required" aria-hidden="true"> *</span>}</label>
    </div>
    <div className="media-field-body">
      <span className={cx('media-thumb', 'media-field-preview', !trimmed && 'is-empty')}>
        {trimmed && kind === 'image' && previewSource && !previewFailed
          ? <img src={previewSource} alt={alt} loading="lazy" onError={() => setPreviewFailed(true)}/>
          : <Icon name={!trimmed ? 'image' : kind === 'document' ? 'picture_as_pdf' : kind === 'video' ? 'movie' : 'broken_image'} size={32}/>}
      </span>
      <div className="media-field-main">
        <div className={cx('field-box', 'has-leading', disabled && 'is-disabled')}>
          <Icon name="image" className="field-leading"/>
          <input
            id={id}
            className="field-input mono"
            value={text}
            disabled={disabled}
            placeholder={t('editor.urlPlaceholder')}
            aria-invalid={error ? true : undefined}
            aria-describedby={`${id}-support`}
            onChange={(event) => { setError(null); write(event.target.value) }}
          />
          {trimmed && !disabled && <IconButton icon="close" label={t('media.field.clear')} onClick={() => { setAsset(null); write('') }}/>}
        </div>
        {asset && <p className="media-field-asset">
          <strong>{mediaDisplayName(asset)}</strong>
          {asset.width && asset.height ? <span className="muted"> · {asset.width}×{asset.height}</span> : null}
          {asset.status === 'archived' && <span className="media-field-warning"> · {t('media.field.archived')}</span>}
          {kind === 'image' && !alt && asset.status === 'active' && <span className="media-field-warning"> · {t('media.noAlt')}</span>}
        </p>}
        {trimmed && !asset && libraryPathFromValue(trimmed) === null && <p className="field-support">{t('media.field.outsideLibrary')}</p>}
        {!disabled && <div className="media-field-actions">
          <Button variant="tonal" small icon="photo_library" onClick={() => setPicking(true)} disabled={progress !== null}>{t('media.field.choose')}</Button>
          {canUpload && <Button variant="text" small icon="upload" onClick={() => fileInput.current?.click()} disabled={progress !== null}>{t('media.field.upload')}</Button>}
          {canUpload && <Button variant="text" small icon="auto_awesome" onClick={() => setGenerating(true)} disabled={progress !== null}>{t('aiGen.field.generate')}</Button>}
          <input ref={fileInput} type="file" accept={ACCEPT_ATTRIBUTE} className="sr-only" tabIndex={-1} aria-hidden="true"
            onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; void upload(file) }}/>
        </div>}
        {progress !== null && <LinearProgress value={progress} label={t('media.upload.progress', { name: label })}/>}
      </div>
    </div>
    <p className={cx('field-support', error && 'field-error-text')} id={`${id}-support`} role={error ? 'alert' : undefined}>{help}</p>
    {generating && <AiImageDialog onUse={choose} onClose={() => setGenerating(false)}/>}
    {picking && <MediaPickerDialog title={t('media.picker.titleFor', { field: label })} onPick={choose} onClose={() => setPicking(false)}/>}
  </div>
}
